import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useEffect } from 'react';

import dayjs from 'dayjs';
import queryString from 'query-string';
import {
  Heading1,
  Heading3,
  InpockView,
  Paragraph1,
  Paragraph2,
  SmallText,
  theme,
} from 'inpock-ui-react';
import { colors } from 'inpock-ui-react/dist/src/color';

import { useOrderGroupDetailQuery, useToast } from '@/hooks';
import { useAuth } from '@/hooks/useAuth';
import { DashDivider, OrderCompleteInfoLine, Spinner, GroupPurchaseBanner } from '@/components';
import { copy, formatPayType } from '@/utils';
import { OrderStatus } from '@/apis';
import { BACKUP_CHECKOUT_INFO } from '@/constants';

import * as S from './styled';

export function PaySuccessPage() {
  const navigate = useNavigate();
  const query = queryString.parse(useLocation().search);
  const { loggedIn } = useAuth();
  const { version } = queryString.parse(location.search);

  const { order_group_number, phone } = query as unknown as {
    order_group_number: string;
    phone: number;
  };

  const { data: order, isFetching } = useOrderGroupDetailQuery({
    orderGroupNumber: order_group_number as string,
    loggedIn,
    phone,
    options: {
      onError: () => navigate('/orders/not-found'),
    },
  });

  const onRouteOrderDetail = () => {
    navigate(
      loggedIn
        ? `/customer/orders/${order_group_number}`
        : `/anonymous/orders/${order_group_number}?phone=${phone}`,
      {
        replace: true,
      },
    );
  };

  const isPaymentComplete = order?.orders.every((order) => order.status !== OrderStatus.UNPAID);

  // 결제 성공 시 세션 스토리지에 저장된 캠페인 아이디 삭제
  useEffect(() => {
    if (isPaymentComplete) {
      sessionStorage.removeItem(BACKUP_CHECKOUT_INFO);
    }
  }, [isPaymentComplete]);

  if (isFetching)
    return (
      <S.LoadingContainer>
        <Heading1>주문 정보 조회 중</Heading1>
        <Heading3 fontWeight="regular" className="mb-16">
          잠시만 기다려주세요
        </Heading3>
        <Spinner size="48px" color="var(--color-ui-orangescale-orange-600)" />
      </S.LoadingContainer>
    );
  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>{isPaymentComplete ? '구매 완료' : '주문 완료'} | 인포크링크</title>
          <meta property="og:title" content="인포크링크 - 가장 편리한 멀티링크 서비스" />
          <meta
            property="og:subscription"
            content="다양한 링크를 모으고 멋진 이야기를 들려주세요!"
          />
        </Helmet>
      </HelmetProvider>
      <InpockView>
        <div className="container">
          <S.CenterBox>
            <S.OrderStatusIcon />
            <Heading1 className="mb-8">
              {isPaymentComplete ? '구매가 완료되었습니다' : '주문이 접수되었습니다'}
            </Heading1>
            <Paragraph1
              color="gray700"
              fontWeight="regular"
              style={{ marginBottom: '20px', textAlign: 'center' }}
            >
              {isPaymentComplete
                ? '판매자의 주문 확인 후 배송이 진행됩니다'
                : '아래 내용을 확인 후 입금해주세요'}
            </Paragraph1>
            <SmallText
              color="gray600"
              style={{ border: '1px solid #eee', padding: '2px 6px', borderRadius: '6px' }}
            >
              주문번호 {order?.order_group_number}
            </SmallText>
            <S.OrderInfo>
              {isPaymentComplete ? (
                <DashDivider color={colors.gray300} sx={{ marginBottom: '20px' }} />
              ) : (
                <S.OrderNumberBox>
                  <DepositInfoLine
                    label="입금계좌"
                    value={{
                      text: `${order?.bank_name} ${order?.account}`,
                      color: 'gray700',
                      isCopied: true,
                    }}
                  />
                  <DepositInfoLine
                    label="입금기한"
                    value={{
                      text: dayjs(order?.expire_date).format('YYYY.MM.DD HH:mm'),
                      color: 'orange800',
                    }}
                  />
                  <DepositInfoLine
                    label="입금 금액"
                    value={{
                      text: `${(order?.amount as number).toLocaleString()}원`,
                      color: 'orange800',
                    }}
                  />
                </S.OrderNumberBox>
              )}
              <OrderCompleteInfoLine label="상품명" content={`${order?.campaign_name}`} />
              <OrderCompleteInfoLine
                label="배송지"
                content={`${order?.receiver_address} ${order?.receiver_detail_address}`}
              />
              {isPaymentComplete ? (
                <>
                  {order?.payment_info.pay_type && (
                    <OrderCompleteInfoLine
                      label="결제 수단"
                      content={formatPayType(order.payment_info)}
                    />
                  )}
                  <OrderCompleteInfoLine
                    label="결제 일시"
                    content={`${dayjs(order?.paid_at).format('YYYY.MM.DD HH:mm')}`}
                  />
                </>
              ) : null}
              <DashDivider color={colors.gray300} sx={{ margin: '12px 0 20px' }} />
              <OrderCompleteInfoLine
                label="총 상품 금액"
                content={`${order?.product_price.toLocaleString()}원`}
              />
              <OrderCompleteInfoLine
                label="배송비"
                content={
                  order?.shipping_fee === 0 ? '무료' : `${order?.shipping_fee.toLocaleString()}원`
                }
              />
              {order?.discount_price ? (
                <OrderCompleteInfoLine
                  label="상품 할인"
                  content={`${order.discount_price?.toLocaleString()}원`}
                />
              ) : null}
              <DashDivider color={colors.gray300} sx={{ margin: '12px 0 20px' }} />
              <OrderCompleteInfoLine
                label="총 결제금액"
                content={`${order?.total_price.toLocaleString()}원`}
                valueColor="orange"
              />
            </S.OrderInfo>
          </S.CenterBox>
          <S.CTAwrapper>
            <S.OrderHistoryButton onClick={onRouteOrderDetail}>
              주문 내역 바로가기
            </S.OrderHistoryButton>
            {version && (
              <S.MarketHomeButton
                onClick={() =>
                  navigate(`/v2/${order?.owner_username}`, {
                    replace: true,
                  })
                }
              >
                마켓 홈으로 이동
              </S.MarketHomeButton>
            )}
          </S.CTAwrapper>
          <S.BannerWrapper>
            <GroupPurchaseBanner utmCampaign={order?.owner_username} utmContent="checkout" />
          </S.BannerWrapper>
        </div>
      </InpockView>
    </>
  );
}

const DepositInfoLine = ({
  label,
  value: { text, color, isCopied = false },
}: {
  label: string;
  value: {
    text: string;
    color: theme.ColorAlias;
    isCopied?: boolean;
  };
}) => {
  const toastOn = useToast();

  const onClick = () => {
    if (copy(text)) {
      return toastOn(`${label}가 복사되었어요 😎`);
    }
  };

  return (
    <S.DepositInfoLineContainer>
      <Paragraph2 color="gray500">{label}</Paragraph2>

      <S.DepositInfoLineContent>
        <Paragraph2 color={color}>{text}</Paragraph2>
        {isCopied && <S.ButtonCopy onClick={onClick} />}
      </S.DepositInfoLineContent>
    </S.DepositInfoLineContainer>
  );
};
